import { useCallback, useEffect } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useAmountConverter } from '../../../hooks/useAmountConverter';
import { useMembershipModalState } from '../../../modalContext';
import type { ModalCallbackProps } from '../../../shared/types';

export function useUserEventListeners({ onDecline }: ModalCallbackProps): void {
	const track = useTracking();
	const [{ amount }] = useMembershipModalState();
	const { toAmountUsd } = useAmountConverter();

	const escapeHandler = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				void track('post_promote_member_offer_submit_escape', {
					amount: toAmountUsd(amount),
					local_amount: amount,
				});
				onDecline();
			}
		},
		[amount, onDecline, track, toAmountUsd],
	);

	const browserBackButtonHandler = useCallback(() => {
		void track('post_promote_member_offer_submit_browser_back_button', {
			amount: toAmountUsd(amount),
			local_amount: amount,
		});
		onDecline();
	}, [amount, onDecline, track, toAmountUsd]);

	const beforeUnloadHandler = useCallback(() => {
		void track('post_promote_member_offer_submit_unload', {
			amount: toAmountUsd(amount),
			local_amount: amount,
		});
	}, [amount, track, toAmountUsd]);

	useEffect(() => {
		document.addEventListener('keydown', escapeHandler);
		// The back button should dismiss the modal rather than navigate backward to ensure
		// payment form elements remain mounted on the page to complete the transaction.
		window.history.pushState(null, '');
		window.addEventListener('popstate', browserBackButtonHandler, { once: true });
		window.addEventListener('beforeunload', beforeUnloadHandler);

		return () => {
			document.removeEventListener('keydown', escapeHandler);
			window.removeEventListener('popstate', browserBackButtonHandler);
			window.removeEventListener('beforeunload', beforeUnloadHandler);
		};
	}, [escapeHandler, browserBackButtonHandler, beforeUnloadHandler]);
}
